import { SimpleGrid, Box, Spinner } from "@chakra-ui/react";
import { addNA } from "App/BsesPoc/components/common/function";
import CustomInput from "components/common/customInput/CustomInput";
import { useEffect, useState, useCallback, useRef } from "react";
import { appRequest } from "routes";

interface CrmTdsGlSectionProps {
  formData: any;
  setFormData: (updates: any | ((prev: any) => any)) => void;
  companyCode: any;
  workflowProcess: any;
  isDisabled?: boolean;
  triggerValidation?: boolean;
}

export default function CrmTdsGlSection({
  formData,
  setFormData,
  companyCode,
  workflowProcess,
  isDisabled = false,
  triggerValidation = false
}: CrmTdsGlSectionProps) {
  const [profitCenterOptions, setProfitCenterOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState({
    profitCenter: false,
    costCenter: false,
  });

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const fetchOptions = useCallback(async (type: string, payload: any) => {
    try {
      const res: any = await appRequest("user", "masterFinancialApi", { ...payload, ...workflowProcess });
      if (res?.status === "success") return res.data;
    } catch (err) {
      console.error(`Failed to fetch ${type}`, err);
    }
    return [];
  }, [workflowProcess]);

  const loadProfitCenters = useCallback(async () => {
    if (!companyCode) return;
    setLoading(l => ({ ...l, profitCenter: true }));
    const data = await fetchOptions("Profit Center", { type: "getProfitCenter", companyCode: companyCode.value || companyCode });
    setProfitCenterOptions((data || []).map((pc: any) => ({ label: pc.label || pc.profitCenter, value: pc.profitCenter })));
    setLoading(l => ({ ...l, profitCenter: false }));
  }, [companyCode, fetchOptions]);

  useEffect(() => {
    loadProfitCenters();
  }, [loadProfitCenters]);

  const updateForm = (updates: any) => {
    setFormData((prev: any) => ({ ...prev, ...updates }));
  };

  const fetchProfitCenter = async (costCenter: string) => {
    const code = typeof companyCode === "object" ? companyCode?.value : companyCode;
    if (!costCenter || !code) return;

    setLoading(l => ({ ...l, costCenter: true }));
    try {
      const payload = {
        companyCode: code,
        costCenter: costCenter,
        processName: "crm"
      };
      const res: any = await appRequest("crmprofitcenter", "get", payload);
      if (res?.status === "success" && Array.isArray(res?.data) && res.data.length > 0) {
        const profitCenter = res.data[0].profitCenter || res.data[0].value || res.data[0].label || "";
        updateForm({ glProfitCenter: profitCenter });
      } else {
        // Not found, store empty but don't force reset here
      }
    } catch (err) {
      console.error("Failed to fetch profit center from cost center", err);
    } finally {
      setLoading(l => ({ ...l, costCenter: false }));
    }
  };

  const lastCostCenter = useRef<string | undefined>(undefined);
  const lastCompanyCode = useRef<string | null>(null);

  // Debounced API call for Cost Center
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    const currentCC = formData.glCostCenter || "";
    const lastCC = lastCostCenter.current;
    
    const code = typeof companyCode === "object" ? companyCode?.value : companyCode;
    const isInitialLoad = lastCompanyCode.current === null;
    const codeChanged = !isInitialLoad && code !== lastCompanyCode.current;

    if (isInitialLoad && code) {
      lastCompanyCode.current = code;
    }

    // Only fetch if CC changed OR Company changed OR (first time AND profit center is empty)
    const ccChanged = lastCC !== undefined && currentCC !== lastCC;
    const needsInitialFetch = lastCC === undefined && !formData.glProfitCenter && currentCC;

    if (codeChanged || ccChanged || needsInitialFetch) {
      if (currentCC.length >= 2) {
        timerRef.current = setTimeout(() => {
          fetchProfitCenter(currentCC);
        }, 500);
      } else if (!currentCC && formData.glProfitCenter && (ccChanged || codeChanged)) {
        updateForm({ glProfitCenter: "" });
      }
    }
    
    lastCostCenter.current = currentCC;
    if (codeChanged) lastCompanyCode.current = code;

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [formData.glCostCenter, companyCode]);

  return (
    <Box p={4} borderWidth="1px" borderRadius="md" bg="gray.50" mt={4}>
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
        <CustomInput
          name="glCostCenter"
          label="Cost Center"
          type="text"
          value={formData.glCostCenter || ""}
          onChange={(e: any) => updateForm({ glCostCenter: e.target.value, glProfitCenter: "" })}
          disabled={isDisabled}
          isRequired={false}
        />

        <Box position="relative">
          <CustomInput
            name="glProfitCenter"
            label="Profit Center"
            type="text"
            value={formData.glProfitCenter || ""}
            onChange={(e: any) => updateForm({ glProfitCenter: e.target.value })}
            disabled={isDisabled}
            isRequired={true}
            placeholder={loading.costCenter ? "Fetching..." : "Profit Center"}
            error="Required"
            showError={triggerValidation && !formData.glProfitCenter}
          />
          {loading.costCenter && (
            <Box position="absolute" right="10px" bottom="10px" zIndex={2}>
              <Spinner size="xs" color="blue.500" />
            </Box>
          )}
        </Box>

        <CustomInput
          name="glPlantCode"
          label="Plant Code"
          value={formData.glPlantCode || ""}
          onChange={(e: any) => updateForm({ glPlantCode: e.target.value })}
          disabled={isDisabled}
          isRequired={true}
          error="Required"
          showError={triggerValidation && !formData.glPlantCode}
        />

        <CustomInput
          name="glBusinessPlace"
          label="Business Place"
          value={formData.glBusinessPlace || ""}
          onChange={(e: any) => updateForm({ glBusinessPlace: e.target.value })}
          disabled={isDisabled}
          isRequired={true}
          error="Required"
          showError={triggerValidation && !formData.glBusinessPlace}
        />

        <CustomInput
          name="glBusinessArea"
          label="Business Area"
          value={formData.glBusinessArea || ""}
          onChange={(e: any) => updateForm({ glBusinessArea: e.target.value })}
          disabled={isDisabled}
          isRequired={true}
          error="Required"
          showError={triggerValidation && !formData.glBusinessArea}
        />

        <CustomInput
          name="glInternalOrder"
          label="Internal Order"
          value={formData.glInternalOrder || ""}
          onChange={(e: any) => updateForm({ glInternalOrder: e.target.value })}
          disabled={isDisabled}
          isRequired={false}
        />

        <CustomInput
          name="glAccount"
          label="GL Account"
          type="text"
          value={formData.glAccount || ""}
          onChange={(e: any) => updateForm({ glAccount: e.target.value })}
          disabled={isDisabled}
          isRequired={true}
          error="Required"
          showError={triggerValidation && !formData.glAccount}
        />

        <CustomInput
          name="glDescription"
          label="GL Description"
          value={formData.glDescription || ""}
          onChange={(e: any) => updateForm({ glDescription: e.target.value })}
          disabled={isDisabled}
          isRequired={true}
          error="Required"
          showError={triggerValidation && !formData.glDescription}
        />

        <CustomInput
          name="glAmount"
          label="GL Amount"
          type="number"
          value={formData.glAmount || ""}
          onChange={(e: any) => updateForm({ glAmount: e.target.value })}
          disabled={isDisabled}
          isRequired={true}
          error="Required"
          showError={triggerValidation && !formData.glAmount}
        />
      </SimpleGrid>
    </Box>
  );
}
